import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import QrCodeImage from "../../../../Components/Common/QrCodeImage";
import {
  MFA_RESEND_COOLDOWN_SEC,
  changeMfaMethod,
  confirmMfa,
  disableMfa,
  fetchMfaStatus,
  regenerateMfaRecoveryCodes,
  resendMfaSetupCode,
  revokeMfaTrustedDevice,
  revokeMfaTrustedDevices,
  sendMfaActionCode,
  setupMfa,
  type MfaMethod,
  type MfaSetupPayload,
  type MfaTrustedDevice,
} from "../../../../helpers/mfa_helper";
import { mergeTenantMeIntoSession } from "../../../../helpers/api_helper";
import { fetchMe } from "../../../../helpers/profile_helper";
import { getLoggedinUser } from "../../../../helpers/api_helper";

const MFA_BANNER_DISMISS_KEY = "mfa_enable_banner_dismissed";

/** Extrai segundos do message de MFA_RESEND_COOLDOWN (LAC-187). */
function cooldownSecondsFromMessage(message: string | undefined): number {
  const match = String(message || "").match(/(\d+)/);
  if (!match) return MFA_RESEND_COOLDOWN_SEC;
  return Math.max(1, Math.min(120, Number(match[1]) || MFA_RESEND_COOLDOWN_SEC));
}

/** Dias restantes até expires_at (UTC ISO) — ceil para não sumir o “1 dia” cedo demais. */
function daysUntilExpiry(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return 0;
  return Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

/**
 * Bloco MFA em Minha conta — TOTP/e-mail, troca de método, device trust.
 */
const AccountMfaSection = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const focusMfa = searchParams.get("focus") === "mfa";
  // E-mail de trust novo: ?trust=1 destaca a lista (além do scroll MFA).
  const focusTrustAlert = searchParams.get("trust") === "1";
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const devicesListRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [method, setMethod] = useState<MfaMethod | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [trustedDevices, setTrustedDevices] = useState(0);
  const [devices, setDevices] = useState<MfaTrustedDevice[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setup, setSetup] = useState<MfaSetupPayload | null>(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [regenCode, setRegenCode] = useState("");
  const [showDisable, setShowDisable] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [pickingMethod, setPickingMethod] = useState(false);
  const [changingMethod, setChangingMethod] = useState(false);
  const [changeCode, setChangeCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const sessionUser = getLoggedinUser()?.user;
  const hasPassword = sessionUser?.has_password !== false;

  const syncSessionMfa = async () => {
    try {
      const me = await fetchMe();
      mergeTenantMeIntoSession(me);
      if (me.user?.mfa_enabled) {
        localStorage.setItem(MFA_BANNER_DISMISS_KEY, "1");
      } else {
        localStorage.removeItem(MFA_BANNER_DISMISS_KEY);
      }
    } catch {
      // Sessão local fica stale até próximo /me.
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await fetchMfaStatus();
      setEnabled(status.enabled);
      setMethod(status.method);
      setRemaining(status.recovery_codes_remaining ?? 0);
      setTrustedDevices(status.trusted_devices ?? 0);
      setDevices(status.devices ?? []);
    } catch (e: any) {
      setError(e?.message || t("settings.account.mfa.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LAC-186: deep-link ?focus=mfa (&trust=1 do e-mail) → rola + toast.
  useEffect(() => {
    if (!focusMfa || loading) return;
    const target =
      focusTrustAlert && trustedDevices > 0
        ? devicesListRef.current
        : sectionRef.current;
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (focusTrustAlert || (enabled && trustedDevices > 0)) {
      toast.info(t("settings.account.mfa.focusTrustToast"), { autoClose: 6000 });
    } else if (enabled) {
      toast.info(t("settings.account.mfa.focusMfaToast"), { autoClose: 4500 });
    }
    // Só na abertura do deep-link (não a cada reload de status).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMfa, focusTrustAlert, loading]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldown]);

  const startResendCooldown = () => setResendCooldown(MFA_RESEND_COOLDOWN_SEC);

  const onStartSetup = async (chosen: MfaMethod) => {
    setBusy(true);
    setError(null);
    setRecoveryCodes(null);
    setPickingMethod(false);
    try {
      const data = await setupMfa(chosen);
      setSetup(data);
      setConfirmCode("");
      if (chosen === "email") {
        startResendCooldown();
        toast.success(t("settings.account.mfa.emailSentToast"));
      }
    } catch (e: any) {
      setError(e?.message || t("settings.account.mfa.setupError"));
    } finally {
      setBusy(false);
    }
  };

  const onBeginChange = async (chosen: MfaMethod) => {
    setBusy(true);
    setError(null);
    try {
      const data = await changeMfaMethod(changeCode.trim(), chosen);
      setSetup(data);
      setChangingMethod(false);
      setChangeCode("");
      setConfirmCode("");
      if (chosen === "email") {
        startResendCooldown();
        toast.success(t("settings.account.mfa.emailSentToast"));
      }
    } catch (e: any) {
      setError(e?.message || t("settings.account.mfa.changeError"));
    } finally {
      setBusy(false);
    }
  };

  const onResendSetup = async () => {
    if (resendCooldown > 0) return;
    setBusy(true);
    setError(null);
    try {
      const data = await resendMfaSetupCode();
      setSetup(data);
      startResendCooldown();
      toast.success(t("settings.account.mfa.emailSentToast"));
    } catch (e: any) {
      setResendCooldown(cooldownSecondsFromMessage(e?.message));
      setError(e?.message || t("settings.account.mfa.setupError"));
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await confirmMfa(confirmCode.trim());
      if (result.recovery_codes?.length) {
        setRecoveryCodes(result.recovery_codes);
        setRemaining(result.recovery_codes.length);
      }
      setMethod(result.method);
      setSetup(null);
      setEnabled(true);
      setTrustedDevices(0);
      await syncSessionMfa();
      toast.success(
        result.message ||
          (result.switched
            ? t("settings.account.mfa.changedToast")
            : t("settings.account.mfa.enabledToast"))
      );
    } catch (err: any) {
      setError(err?.message || t("settings.account.mfa.confirmError"));
    } finally {
      setBusy(false);
    }
  };

  const onSendActionCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await sendMfaActionCode();
      startResendCooldown();
      toast.success(t("settings.account.mfa.emailSentToast"));
    } catch (e: any) {
      setResendCooldown(cooldownSecondsFromMessage(e?.message));
      setError(e?.message || t("settings.account.mfa.setupError"));
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await disableMfa(
        hasPassword ? disablePassword : null,
        disableCode.trim()
      );
      setEnabled(false);
      setMethod(null);
      setRemaining(0);
      setTrustedDevices(0);
      setShowDisable(false);
      setDisablePassword("");
      setDisableCode("");
      setRecoveryCodes(null);
      await syncSessionMfa();
      toast.success(result.message || t("settings.account.mfa.disabledToast"));
    } catch (err: any) {
      setError(err?.message || t("settings.account.mfa.disableError"));
    } finally {
      setBusy(false);
    }
  };

  const onRegen = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await regenerateMfaRecoveryCodes(regenCode.trim());
      setRecoveryCodes(result.recovery_codes);
      setRemaining(result.recovery_codes.length);
      setShowRegen(false);
      setRegenCode("");
      toast.success(result.message || t("settings.account.mfa.regenToast"));
    } catch (err: any) {
      setError(err?.message || t("settings.account.mfa.regenError"));
    } finally {
      setBusy(false);
    }
  };

  const onRevokeTrust = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await revokeMfaTrustedDevices();
      setTrustedDevices(result.trusted_devices);
      setDevices(result.devices ?? []);
      toast.success(result.message || t("settings.account.mfa.trustRevokedToast"));
    } catch (e: any) {
      setError(e?.message || t("settings.account.mfa.trustRevokeError"));
    } finally {
      setBusy(false);
    }
  };

  const onRevokeOneDevice = async (deviceId: number) => {
    setBusy(true);
    setError(null);
    try {
      const result = await revokeMfaTrustedDevice(deviceId);
      setTrustedDevices(result.trusted_devices);
      setDevices(result.devices ?? []);
      toast.success(
        result.message || t("settings.account.mfa.trustOneRevokedToast")
      );
    } catch (e: any) {
      setError(e?.message || t("settings.account.mfa.trustRevokeError"));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    if (!recoveryCodes?.length) return;
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast.success(t("settings.account.mfa.copied"));
    } catch {
      toast.error(t("settings.account.mfa.copyFailed"));
    }
  };

  const downloadCodes = () => {
    if (!recoveryCodes?.length) return;
    const blob = new Blob(
      [
        `${t("settings.account.mfa.recoveryTitle")}\n`,
        `${t("settings.account.mfa.recoveryHint")}\n\n`,
        recoveryCodes.join("\n"),
        "\n",
      ],
      { type: "text/plain;charset=utf-8" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "brenvio-codigos-recuperacao.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const codeLabel =
    method === "email" || setup?.method === "email"
      ? t("settings.account.mfa.codeLabelEmail")
      : t("settings.account.mfa.codeLabel");

  const otherMethod: MfaMethod | null =
    method === "totp" ? "email" : method === "email" ? "totp" : null;

  if (loading) {
    return (
      <Card className="mb-3">
        <CardBody className="text-center py-4">
          <Spinner size="sm" className="me-2" />
          {t("settings.account.loading")}
        </CardBody>
      </Card>
    );
  }

  return (
    <div ref={sectionRef} id="account-mfa">
    <Card className="mb-3">
      <CardHeader>
        <h5 className="card-title mb-0">{t("settings.account.mfa.title")}</h5>
      </CardHeader>
      <CardBody>
        <p className="text-muted small mb-3">{t("settings.account.mfa.subtitle")}</p>

        {error ? (
          <Alert color="danger" className="py-2">
            {error}
          </Alert>
        ) : null}

        {recoveryCodes ? (
          <Alert color="warning" className="mb-3">
            <strong className="d-block mb-2">{t("settings.account.mfa.recoveryTitle")}</strong>
            <p className="small mb-2">{t("settings.account.mfa.recoveryHint")}</p>
            <ul className="mb-2 font-monospace small">
              {recoveryCodes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <Button color="secondary" size="sm" outline type="button" onClick={copyCodes}>
              {t("settings.account.mfa.copyCodes")}
            </Button>
            <Button
              color="secondary"
              size="sm"
              outline
              type="button"
              className="ms-2"
              onClick={downloadCodes}
            >
              {t("settings.account.mfa.downloadCodes")}
            </Button>
            <Button
              color="link"
              size="sm"
              type="button"
              className="ms-2"
              onClick={() => setRecoveryCodes(null)}
            >
              {t("settings.account.mfa.dismissRecovery")}
            </Button>
          </Alert>
        ) : null}

        {enabled && !setup ? (
          <>
            <Alert color="success" className="py-2">
              {method === "email"
                ? t("settings.account.mfa.enabledStatusEmail")
                : t("settings.account.mfa.enabledStatus")}
            </Alert>
            {remaining < 3 ? (
              <Alert color="warning" className="py-2">
                {t("settings.account.mfa.lowRecovery", { count: remaining })}
              </Alert>
            ) : null}
            {trustedDevices > 0 ? (
              <div
                ref={devicesListRef}
                className={
                  focusTrustAlert
                    ? "mb-3 rounded border border-warning border-2 p-2"
                    : "mb-3"
                }
              >
                <p className="small text-muted mb-2">
                  {t("settings.account.mfa.trustedCount", {
                    count: trustedDevices,
                  })}
                </p>
                <ul className="list-group list-group-flush border rounded mb-2">
                  {devices.map((d) => {
                    const daysLeft = daysUntilExpiry(d.expires_at);
                    return (
                      <li
                        key={d.id}
                        className="list-group-item d-flex flex-wrap justify-content-between align-items-start gap-2 py-2"
                      >
                        <div className="small">
                          <div className="fw-medium">
                            {d.label ||
                              t("settings.account.mfa.trustedUnknown")}
                          </div>
                          {d.last_used_at ? (
                            <div className="text-muted">
                              {t("settings.account.mfa.trustedLastUsed", {
                                at: new Date(d.last_used_at).toLocaleString(),
                              })}
                            </div>
                          ) : null}
                          {daysLeft !== null ? (
                            <div className="text-muted">
                              {daysLeft === 0
                                ? t("settings.account.mfa.trustedExpiresToday")
                                : t("settings.account.mfa.trustedExpiresIn", {
                                    days: daysLeft,
                                  })}
                            </div>
                          ) : null}
                        </div>
                        <Button
                          color="light"
                          size="sm"
                          type="button"
                          disabled={busy}
                          onClick={() => void onRevokeOneDevice(d.id)}
                        >
                          {t("settings.account.mfa.forgetOne")}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            <div className="d-flex flex-wrap gap-2">
              {otherMethod ? (
                <Button
                  color="light"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setChangingMethod((v) => !v);
                    setShowDisable(false);
                    setShowRegen(false);
                  }}
                >
                  {t("settings.account.mfa.changeMethodButton")}
                </Button>
              ) : null}
              <Button
                color="light"
                type="button"
                disabled={busy}
                onClick={() => {
                  setShowRegen((v) => !v);
                  setShowDisable(false);
                  setChangingMethod(false);
                }}
              >
                {t("settings.account.mfa.regenButton")}
              </Button>
              {trustedDevices > 0 ? (
                <Button
                  color="light"
                  type="button"
                  disabled={busy}
                  onClick={() => void onRevokeTrust()}
                >
                  {t("settings.account.mfa.forgetDevices")}
                </Button>
              ) : null}
              <Button
                color="danger"
                outline
                type="button"
                disabled={busy}
                onClick={() => {
                  setShowDisable((v) => !v);
                  setShowRegen(false);
                  setChangingMethod(false);
                }}
              >
                {t("settings.account.mfa.disableButton")}
              </Button>
            </div>

            {changingMethod && otherMethod ? (
              <div className="mt-3 border rounded p-3">
                <p className="small mb-2">{t("settings.account.mfa.changeMethodHint")}</p>
                {method === "email" ? (
                  <Button
                    color="link"
                    size="sm"
                    type="button"
                    className="px-0 mb-2"
                    disabled={busy || resendCooldown > 0}
                    onClick={() => void onSendActionCode()}
                  >
                    {resendCooldown > 0
                      ? t("settings.account.mfa.resendWait", { seconds: resendCooldown })
                      : t("settings.account.mfa.sendEmailCode")}
                  </Button>
                ) : null}
                <Label htmlFor="mfa-change-code">{codeLabel}</Label>
                <Input
                  id="mfa-change-code"
                  value={changeCode}
                  onChange={(e) => setChangeCode(e.target.value)}
                  autoComplete="one-time-code"
                  className="mb-2"
                />
                <Button
                  color="primary"
                  type="button"
                  disabled={busy || !changeCode.trim()}
                  onClick={() => void onBeginChange(otherMethod)}
                >
                  {busy ? (
                    <Spinner size="sm" />
                  ) : otherMethod === "email" ? (
                    t("settings.account.mfa.switchToEmail")
                  ) : (
                    t("settings.account.mfa.switchToTotp")
                  )}
                </Button>
              </div>
            ) : null}

            {showRegen ? (
              <Form onSubmit={onRegen} className="mt-3 border rounded p-3">
                {method === "email" ? (
                  <Button
                    color="link"
                    size="sm"
                    type="button"
                    className="px-0 mb-2"
                    disabled={busy || resendCooldown > 0}
                    onClick={() => void onSendActionCode()}
                  >
                    {resendCooldown > 0
                      ? t("settings.account.mfa.resendWait", { seconds: resendCooldown })
                      : t("settings.account.mfa.sendEmailCode")}
                  </Button>
                ) : null}
                <Label htmlFor="mfa-regen-code">{codeLabel}</Label>
                <Input
                  id="mfa-regen-code"
                  value={regenCode}
                  onChange={(e) => setRegenCode(e.target.value)}
                  autoComplete="one-time-code"
                  className="mb-2"
                  required
                />
                <Button color="primary" type="submit" disabled={busy || !regenCode.trim()}>
                  {busy ? <Spinner size="sm" /> : t("settings.account.mfa.regenSubmit")}
                </Button>
              </Form>
            ) : null}

            {showDisable ? (
              <Form onSubmit={onDisable} className="mt-3 border rounded p-3">
                {!hasPassword ? (
                  <Alert color="info" className="py-2 small">
                    {t("settings.account.mfa.disableGoogleOnlyHint")}
                  </Alert>
                ) : (
                  <div className="mb-2">
                    <Label htmlFor="mfa-disable-password">
                      {t("settings.account.currentPassword")}
                    </Label>
                    <Input
                      id="mfa-disable-password"
                      type="password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </div>
                )}
                {method === "email" ? (
                  <Button
                    color="link"
                    size="sm"
                    type="button"
                    className="px-0 mb-2"
                    disabled={busy || resendCooldown > 0}
                    onClick={() => void onSendActionCode()}
                  >
                    {resendCooldown > 0
                      ? t("settings.account.mfa.resendWait", { seconds: resendCooldown })
                      : t("settings.account.mfa.sendEmailCode")}
                  </Button>
                ) : null}
                <div className="mb-2">
                  <Label htmlFor="mfa-disable-code">{codeLabel}</Label>
                  <Input
                    id="mfa-disable-code"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    autoComplete="one-time-code"
                    required
                  />
                </div>
                <Button
                  color="danger"
                  type="submit"
                  disabled={
                    busy ||
                    !disableCode.trim() ||
                    (hasPassword && !disablePassword)
                  }
                >
                  {busy ? <Spinner size="sm" /> : t("settings.account.mfa.disableSubmit")}
                </Button>
              </Form>
            ) : null}
          </>
        ) : null}

        {!enabled && !setup && !pickingMethod ? (
          <Button
            color="primary"
            type="button"
            disabled={busy}
            onClick={() => setPickingMethod(true)}
          >
            {t("settings.account.mfa.enableButton")}
          </Button>
        ) : null}

        {!enabled && !setup && pickingMethod ? (
          <div className="border rounded p-3">
            <p className="small mb-3">{t("settings.account.mfa.chooseMethod")}</p>
            <div className="d-flex flex-wrap gap-2 align-items-start">
              <div>
                <Button
                  color="primary"
                  type="button"
                  disabled={busy}
                  onClick={() => void onStartSetup("totp")}
                >
                  {busy ? <Spinner size="sm" /> : t("settings.account.mfa.methodTotp")}
                </Button>
                <div className="mt-1">
                  <Badge color="success" pill>
                    {t("settings.account.mfa.recommendedBadge")}
                  </Badge>
                </div>
              </div>
              <Button
                color="primary"
                outline
                type="button"
                disabled={busy}
                onClick={() => void onStartSetup("email")}
              >
                {t("settings.account.mfa.methodEmail")}
              </Button>
              <Button
                color="light"
                type="button"
                disabled={busy}
                onClick={() => setPickingMethod(false)}
              >
                {t("settings.account.mfa.cancelSetup")}
              </Button>
            </div>
            <p className="small text-muted mt-3 mb-0">
              {t("settings.account.mfa.chooseMethodHint")}
            </p>
          </div>
        ) : null}

        {setup?.method === "totp" && setup.otpauth_url && setup.secret ? (
          <Form onSubmit={onConfirm} className="mt-2">
            {setup.switching ? (
              <Alert color="info" className="py-2 small">
                {t("settings.account.mfa.switchingHint")}
              </Alert>
            ) : null}
            <Row className="align-items-start g-3">
              <Col md="auto">
                <QrCodeImage
                  value={setup.otpauth_url}
                  alt={t("settings.account.mfa.qrAlt")}
                />
              </Col>
              <Col>
                <p className="small mb-2">{t("settings.account.mfa.scanHint")}</p>
                <p className="small text-muted mb-1">{t("settings.account.mfa.manualSecret")}</p>
                <code className="d-block mb-3 user-select-all">{setup.secret}</code>
                <Label htmlFor="mfa-confirm-code">{t("settings.account.mfa.codeLabel")}</Label>
                <Input
                  id="mfa-confirm-code"
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  className="mb-2"
                  required
                />
                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    type="submit"
                    disabled={busy || !confirmCode.trim()}
                  >
                    {busy ? <Spinner size="sm" /> : t("settings.account.mfa.confirmButton")}
                  </Button>
                  <Button
                    color="light"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setSetup(null);
                      setConfirmCode("");
                    }}
                  >
                    {t("settings.account.mfa.cancelSetup")}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        ) : null}

        {setup?.method === "email" ? (
          <Form onSubmit={onConfirm} className="mt-2 border rounded p-3">
            <Alert color="warning" className="py-2 small">
              {t("settings.account.mfa.emailSameChannelWarning")}
            </Alert>
            {setup.switching ? (
              <Alert color="info" className="py-2 small">
                {t("settings.account.mfa.switchingHint")}
              </Alert>
            ) : null}
            <p className="small mb-2">
              {t("settings.account.mfa.emailSetupHint", {
                email: setup.email_hint || "",
              })}
            </p>
            <Label htmlFor="mfa-confirm-email-code">
              {t("settings.account.mfa.codeLabelEmail")}
            </Label>
            <Input
              id="mfa-confirm-email-code"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              className="mb-2"
              required
            />
            <div className="d-flex flex-wrap gap-2">
              <Button
                color="primary"
                type="submit"
                disabled={busy || !confirmCode.trim()}
              >
                {busy ? <Spinner size="sm" /> : t("settings.account.mfa.confirmButton")}
              </Button>
              <Button
                color="link"
                type="button"
                disabled={busy || resendCooldown > 0}
                onClick={() => void onResendSetup()}
              >
                {resendCooldown > 0
                  ? t("settings.account.mfa.resendWait", { seconds: resendCooldown })
                  : t("settings.account.mfa.resendEmail")}
              </Button>
              <Button
                color="light"
                type="button"
                disabled={busy}
                onClick={() => {
                  setSetup(null);
                  setConfirmCode("");
                }}
              >
                {t("settings.account.mfa.cancelSetup")}
              </Button>
            </div>
          </Form>
        ) : null}
      </CardBody>
    </Card>
    </div>
  );
};

export default AccountMfaSection;
